import { useState, useEffect, useMemo } from 'react';
import { Button, AppQrCode } from '@atoms/index';
import { AppStoreButtons } from '@molecules/index';
import { storage } from '@utils/storage';
import { resolveMobileAppDownloadUrl } from '../../constants/mobileAppLinks';

const BANNER_DISMISSED_KEY = 'lezzet_yolda_mobile_banner_dismissed';
const SHOW_AFTER_MS = 5000;

const MobileAppBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const downloadUrl = useMemo(() => resolveMobileAppDownloadUrl(), []);

  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) return;

    const isDismissed = storage.get<boolean>(BANNER_DISMISSED_KEY);
    if (isDismissed) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, SHOW_AFTER_MS);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    storage.set(BANNER_DISMISSED_KEY, true);
  };

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-40 animate-slide-up">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-4 px-4 shadow-2xl">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="/favicon/android-chrome-192x192.png"
                  alt="Lezzet Yolda Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <p className="font-bold text-lg">Lezzet Yolda Mobil Uygulaması</p>
                <p className="text-gray-300 text-sm">Uygulamaya özel fırsatları kaçırma!</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={handleOpenModal}
                className="bg-white text-gray-900 hover:bg-gray-100"
              >
                📱 İndir
              </Button>
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-white p-2 transition-colors"
                aria-label="Kapat"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 z-0 bg-black/60 backdrop-blur-sm"
            onClick={handleCloseModal}
            aria-hidden="true"
          />

          <div
            className="relative z-10 bg-gray-900 rounded-3xl p-8 max-w-md w-full shadow-2xl animate-scale-in"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-app-download-title"
          >
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-white p-2 transition-colors"
              aria-label="Kapat"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="flex justify-center mb-6">
              <div className="bg-white p-4 rounded-2xl shadow-inner">
                <div className="relative">
                  <AppQrCode value={downloadUrl} size={200} className="block" />

                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-14 h-14 rounded-xl overflow-hidden shadow-lg border-4 border-white bg-white">
                      <img
                        src="/favicon/apple-touch-icon.png"
                        alt=""
                        aria-hidden="true"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mb-6">
              <h2 id="mobile-app-download-title" className="text-white text-xl font-bold mb-2">
                Uygulamaya özel fırsatları yakalayın.
              </h2>
              <p className="text-gray-400">Hemen indirin.</p>
            </div>

            <AppStoreButtons />
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default MobileAppBanner;
